// Shared agent-form building blocks used by BOTH the create dialog (MorrowAgentsListPage) and
// the details modal (MorrowAgentWindowPage) so they are identical and can't drift.
import {
  BrainIcon,
  CheckIcon,
  EyeIcon,
  FileTextIcon,
  FolderIcon,
  GlobeIcon,
  MailIcon,
  MonitorIcon,
  ShieldCheckIcon,
  UserRoundIcon,
  WrenchIcon,
  ZapIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { apiUrl } from "../../apiBase";
import { getOwnerId } from "../../clerkTokenBridge";
import { resolveComposioUserId } from "../../composioConnect";
import { type AgentAutonomy } from "../../morrowAgents";
import { resolveContextToolIcon } from "../context/contextToolIcon";
import { builtinAppLabel, builtinAppLogo } from "../settings/builtinApps";
import { AgentAvatar, isImage, MascotSvg, PRESET_MASCOTS } from "./AgentAvatar";
import { presetMascotBody } from "./agentHue";

export const FORM_INPUT =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground " +
  "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring/60";

/** Icons for the built-in abilities. */
const CAP_ICON: Record<string, typeof GlobeIcon> = {
  cloudBrowser: GlobeIcon,
  browser: MonitorIcon,
  vault: FolderIcon,
  memoryWrite: BrainIcon,
  email: MailIcon,
};

/** The built-in ability keys + human labels (persisted as the agent's `capabilities`). */
export const CAP_KEYS: ReadonlyArray<[string, string]> = [
  ["cloudBrowser", "Cloud browser"],
  ["browser", "Local browser"],
  ["vault", "Vault / files"],
  ["memoryWrite", "Memory"],
  ["email", "Email"],
];

/** Clear labels + one-line descriptions for each ability (shown in both modals). */
export const CAP_META: Record<string, { label: string; desc: string }> = {
  cloudBrowser: { label: "Web browser", desc: "Browse and act on the web in a live browser" },
  browser: { label: "Local browser", desc: "Drive a browser on your own machine" },
  vault: { label: "Files & vault", desc: "Read and write files in its workspace" },
  memoryWrite: { label: "Memory", desc: "Remember things across conversations" },
  email: { label: "Email", desc: "Send email on your behalf (asks first unless autonomous)" },
};

/** The agent charter (`instruction`) shown as two editable files. Composed/parsed losslessly. */
export function splitCharter(instruction: string): { soul: string; purpose: string } {
  const text = (instruction ?? "").trim();
  const m = text.match(
    /^#{1,6}\s*soul(?:\.md)?\s*\n([\s\S]*?)\n#{1,6}\s*purpose(?:\.md)?\s*\n([\s\S]*)$/i,
  );
  if (m) return { soul: (m[1] ?? "").trim(), purpose: (m[2] ?? "").trim() };
  return { soul: text, purpose: "" };
}
export function joinCharter(soul: string, purpose: string): string {
  const s = soul.trim();
  const p = purpose.trim();
  if (!s && !p) return "";
  if (!p) return s;
  return `# soul.md\n${s}\n\n# purpose.md\n${p}`;
}

/** A clean on-brand toggle switch. */
export function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
        on ? "bg-primary" : "bg-muted-foreground/25"
      }`}
    >
      <span
        className={`inline-block size-4 transform rounded-full bg-white shadow-sm transition-transform ${
          on ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

/** The bordered group of ability rows (label + description + toggle). */
export function CapabilityToggles({
  caps,
  onChange,
}: {
  caps: Record<string, boolean>;
  onChange: (key: string, on: boolean) => void;
}) {
  return (
    <div className="flex flex-col divide-y divide-border/60 overflow-hidden rounded-xl border border-border">
      {CAP_KEYS.map(([key]) => {
        const meta = CAP_META[key] ?? { label: key, desc: "" };
        return (
          <div key={key} className="flex items-center gap-3 px-3 py-2.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              {(() => {
                const Icon = CAP_ICON[key] ?? WrenchIcon;
                return <Icon className="size-4" />;
              })()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-foreground">{meta.label}</div>
              <div className="text-xs text-muted-foreground">{meta.desc}</div>
            </div>
            <Toggle on={caps[key] ?? true} onChange={(v) => onChange(key, v)} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The apps this person has connected, so an agent can only be granted what it can reach.
 *
 * Reads BOTH sources. Composio toolkits come from its connections endpoint; our own connectors
 * (the marketplaces, DIA, Entegra) live in a separate table and were missing here entirely —
 * so they could never be granted to an agent. That was not merely a gap: `allowed_apps` is
 * enforced by naming the permitted apps in the agent's preamble ("use ONLY these"), so an agent
 * restricted to, say, Gmail was implicitly told NOT to touch Trendyol even though those tools
 * were loaded for it. Marketplace work then failed for a reason nothing on screen explained.
 */
export function useConnectedApps(active: boolean): readonly string[] {
  const [connected, setConnected] = useState<readonly string[]>([]);
  useEffect(() => {
    if (!active) return;
    let alive = true;
    void (async () => {
      const userId = resolveComposioUserId(getOwnerId());
      const read = async <T,>(path: string): Promise<T | null> => {
        try {
          const res = await fetch(apiUrl(path), {
            headers: { accept: "application/json" },
            credentials: "include",
          });
          return res.ok ? ((await res.json()) as T) : null;
        } catch {
          return null;
        }
      };
      // Independent sources: one failing must not hide the other's apps.
      const [composio, builtin] = await Promise.all([
        read<{ connected?: unknown }>(
          `/api/morrow/composio/connections?userId=${encodeURIComponent(userId)}`,
        ),
        read<{ connections?: { appId?: unknown; status?: unknown }[] }>(
          `/api/morrow/apps/connections`,
        ),
      ]);
      const slugs = Array.isArray(composio?.connected)
        ? composio.connected.filter((s): s is string => typeof s === "string")
        : [];
      const builtinIds = (builtin?.connections ?? [])
        .filter((c) => typeof c.appId === "string" && c.status !== "error")
        .map((c) => c.appId as string);
      if (alive) setConnected([...new Set([...slugs, ...builtinIds])]);
    })();
    return () => {
      alive = false;
    };
  }, [active]);
  return connected;
}

/** Pick which of the user's CONNECTED apps this agent may use. You can only pick connected apps —
 * connect new ones in Settings first (selecting a disconnected app it can't reach is meaningless). */
export function AppsPicker({
  connected,
  selected,
  onChange,
}: {
  connected: readonly string[];
  selected: readonly string[];
  onChange: (slugs: readonly string[]) => void;
}) {
  const [q, setQ] = useState("");
  const toggle = (slug: string) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    onChange([...next]);
  };
  if (connected.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground">
        No apps connected yet — connect apps in <span className="text-foreground">Settings</span>,
        then choose which ones this agent may use.
      </div>
    );
  }
  // Match the display name as well as the slug: a seller searching "Çiçeksepeti" should not
  // have to know the app's id is "ciceksepeti".
  const needle = q.trim().toLocaleLowerCase("tr");
  const filtered = connected.filter((s) =>
    `${s} ${builtinAppLabel(s) ?? ""}`.toLocaleLowerCase("tr").includes(needle),
  );
  return (
    <div className="flex flex-col gap-2">
      {connected.length > 6 ? (
        <input
          className={FORM_INPUT}
          placeholder="Filter your connected apps…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {filtered.map((slug) => {
          const on = selected.includes(slug);
          // Composio's icon resolver knows nothing about our own connectors, so fall back to
          // their local name and logo — otherwise the chip reads "ciceksepeti" with no image.
          const composioIcon = resolveContextToolIcon(slug);
          const builtinName = builtinAppLabel(slug);
          const logoUrl = composioIcon.logoUrl ?? (builtinName ? builtinAppLogo(slug) : undefined);
          const appName = composioIcon.appName ?? builtinName ?? slug;
          return (
            <button
              key={slug}
              type="button"
              onClick={() => toggle(slug)}
              aria-pressed={on}
              className={`inline-flex items-center gap-1.5 rounded-full border py-1 pl-1.5 pr-2.5 text-xs transition ${
                on
                  ? "border-primary/60 bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              {logoUrl ? (
                <img src={logoUrl} alt="" className="size-4 rounded-sm object-contain" />
              ) : null}
              {appName}
              {on ? <CheckIcon className="size-3 text-primary" /> : null}
            </button>
          );
        })}
        {filtered.length === 0 ? (
          <span className="text-xs text-muted-foreground">No match.</span>
        ) : null}
      </div>
      <div className="text-[11px] text-muted-foreground">
        {selected.length === 0
          ? "None selected = it can't use any apps. Pick the ones it may use."
          : `${selected.length} selected — it's limited to these.`}
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[13px] font-medium text-foreground">{label}</div>
      {hint ? (
        <div className="-mt-1 text-xs leading-relaxed text-muted-foreground">{hint}</div>
      ) : null}
      {children}
    </div>
  );
}

/** A charter "file" (soul.md / purpose.md) — an always-open editor with its filename on top. */
export function FileEditorCard({
  filename,
  hint,
  value,
  onChange,
}: {
  filename: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card focus-within:border-ring/60 focus-within:ring-2 focus-within:ring-ring/30">
      <span className="flex items-center gap-2 border-b border-border bg-muted/50 px-3 py-2">
        <FileTextIcon className="size-3.5 text-muted-foreground" />
        <span className="font-mono text-xs font-medium text-foreground">{filename}</span>
        <span className="ml-auto truncate text-[11px] text-muted-foreground">{hint}</span>
      </span>
      <textarea
        className="min-h-[180px] w-full resize-y bg-transparent px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          filename === "soul.md"
            ? "You are Ravi, calm and precise. You write short, warm messages…"
            : "Fill open roles: source candidates on LinkedIn, book screens, keep the tracker current…"
        }
      />
    </label>
  );
}

/** Pick the agent's character: a live preview plus the preset characters, drawn as SVG. */
function MascotPicker({
  value,
  name,
  onChange,
}: {
  value: string;
  name: string;
  onChange: (avatar: string) => void;
}) {
  const custom = !PRESET_MASCOTS.includes(value);
  const previewBody = presetMascotBody(value);
  return (
    <div className="flex items-center gap-4">
      <div
        className="flex size-24 shrink-0 items-center justify-center rounded-2xl"
        style={{
          background: previewBody
            ? `color-mix(in oklab, ${previewBody} 16%, var(--card))`
            : "var(--muted)",
        }}
      >
        {previewBody ? (
          <span className="agent-mascot-bob block">
            <MascotSvg color={previewBody} size={56} />
          </span>
        ) : value && isImage(value) ? (
          <img src={value} alt="" className="size-16 rounded-xl object-cover" />
        ) : (
          <AgentAvatar name={name || "Agent"} avatar={value} size={56} round />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Character">
          {PRESET_MASCOTS.map((m) => {
            const body = presetMascotBody(m)!;
            const on = value === m;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={m.replace(/^.*\/(\w+)\.png$/, "$1")}
                onClick={() => onChange(m)}
                className={`flex size-12 items-center justify-center rounded-xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  on ? "" : "hover:bg-accent"
                }`}
                style={
                  on
                    ? {
                        background: `color-mix(in oklab, ${body} 18%, var(--card))`,
                        boxShadow: `inset 0 0 0 2px ${body}`,
                      }
                    : undefined
                }
              >
                <MascotSvg color={body} size={30} glow={false} />
              </button>
            );
          })}
        </div>
        <input
          className={`${FORM_INPUT} h-8 text-xs`}
          placeholder="Or use an emoji or image link"
          value={custom ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

const AUTONOMY_OPTIONS: ReadonlyArray<{
  id: AgentAutonomy;
  title: string;
  desc: string;
  Icon: typeof EyeIcon;
}> = [
  { id: "read", title: "Read-only", desc: "Looks and proposes. Never acts.", Icon: EyeIcon },
  {
    id: "approval",
    title: "Ask first",
    desc: "Asks you before sending, paying or deleting.",
    Icon: ShieldCheckIcon,
  },
  {
    id: "autonomous",
    title: "Autonomous",
    desc: "Acts on its own within its mandate.",
    Icon: ZapIcon,
  },
];

/** The values every agent form edits (create + details share this shape). */
export interface AgentFormValues {
  avatar: string;
  name: string;
  role: string;
  soul: string;
  purpose: string;
  autonomy: AgentAutonomy;
  allowedApps: readonly string[];
  caps: Record<string, boolean>;
  /** "Keep working" (proactive self-continuation) opt-in — default off. */
  keepWorking: boolean;
  /** Daily hands-on-time budget in MINUTES (0/empty = none). Stored as seconds on the agent. */
  dailyBudgetMinutes: number;
}

/** The three shared tabs (Identity / Tools / Autonomy) with identical fields in both modals.
 * `extraTabs` lets the details modal append its own tab (e.g. Activity). */
export function AgentTabs({
  values,
  patch,
  connected,
  extraTabs = [],
}: {
  values: AgentFormValues;
  patch: (p: Partial<AgentFormValues>) => void;
  connected: readonly string[];
  extraTabs?: ReadonlyArray<{
    id: string;
    label: string;
    Icon: typeof WrenchIcon;
    content: ReactNode;
  }>;
}) {
  const baseTabs = [
    { id: "identity", label: "Identity", Icon: UserRoundIcon },
    { id: "tools", label: "Tools", Icon: WrenchIcon },
    { id: "autonomy", label: "Autonomy", Icon: ShieldCheckIcon },
  ] as const;
  const tabs = [...baseTabs, ...extraTabs.map((t) => ({ id: t.id, label: t.label, Icon: t.Icon }))];
  const [tab, setTab] = useState<string>("identity");
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex w-full items-center gap-1 rounded-xl bg-muted p-1" role="tablist">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition ${
              tab === id
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="size-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
        {tab === "identity" ? (
          <>
            <Field label="Character">
              <MascotPicker
                value={values.avatar}
                name={values.name}
                onChange={(avatar) => patch({ avatar })}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name">
                <input
                  className={FORM_INPUT}
                  placeholder="e.g. Ravi — HR"
                  value={values.name}
                  onChange={(e) => patch({ name: e.target.value })}
                />
              </Field>
              <Field label="Role">
                <input
                  className={FORM_INPUT}
                  placeholder="e.g. Head of Recruiting"
                  value={values.role}
                  onChange={(e) => patch({ role: e.target.value })}
                />
              </Field>
            </div>
            <Field
              label="Charter"
              hint="Both files go into the agent's instructions on every turn and every background run."
            >
              <div className="grid gap-3 md:grid-cols-2">
                <FileEditorCard
                  filename="soul.md"
                  hint="Who it is"
                  value={values.soul}
                  onChange={(v) => patch({ soul: v })}
                />
                <FileEditorCard
                  filename="purpose.md"
                  hint="What it's for"
                  value={values.purpose}
                  onChange={(v) => patch({ purpose: v })}
                />
              </div>
            </Field>
          </>
        ) : null}

        {tab === "tools" ? (
          <>
            <Field label="Built-in abilities" hint="What this agent can do on its own.">
              <CapabilityToggles
                caps={values.caps}
                onChange={(key, v) => patch({ caps: { ...values.caps, [key]: v } })}
              />
            </Field>
            <Field label="Connected apps" hint="Pick which of your connected apps it may use.">
              <AppsPicker
                connected={connected}
                selected={values.allowedApps}
                onChange={(slugs) => patch({ allowedApps: slugs })}
              />
            </Field>
          </>
        ) : null}

        {tab === "autonomy" ? (
          <>
            <Field label="Autonomy" hint="How much it can do without checking with you.">
              <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Autonomy">
                {AUTONOMY_OPTIONS.map(({ id, title, desc, Icon }) => {
                  const on = values.autonomy === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => patch({ autonomy: id })}
                      className={`flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
                        on ? "border-primary/60 bg-primary/5" : "border-border hover:bg-accent"
                      }`}
                    >
                      <Icon className={`size-4 ${on ? "text-primary" : "text-muted-foreground"}`} />
                      <span className="text-sm font-medium text-foreground">{title}</span>
                      <span className="text-xs leading-snug text-muted-foreground">{desc}</span>
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field
              label="Keep working (proactive)"
              hint="On: this agent keeps advancing its mandate on its own and messages you when it needs input — instead of waiting for you. Bounded by the daily budget below; you can Pause it anytime."
            >
              <div className="flex items-center gap-2.5 rounded-xl border border-border px-3 py-2.5">
                <Toggle on={values.keepWorking} onChange={(v) => patch({ keepWorking: v })} />
                <span className="text-xs text-muted-foreground">
                  {values.keepWorking ? "On — works on its own" : "Off — reactive (waits for you)"}
                </span>
              </div>
            </Field>
            <Field
              label="Daily work budget"
              hint="The most hands-on time this agent may spend per day. It pauses when that's used up (resume or raise it anytime). Set this before turning on Keep working."
            >
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step={5}
                  className={FORM_INPUT}
                  value={values.dailyBudgetMinutes || ""}
                  onChange={(e) =>
                    patch({
                      dailyBudgetMinutes: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                    })
                  }
                  placeholder="e.g. 60"
                />
                <span className="shrink-0 text-xs text-muted-foreground">minutes / day</span>
              </div>
            </Field>
            <div className="rounded-xl bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
              <div className="mb-1 font-medium text-foreground">
                Always-on &amp; self-scheduling
              </div>
              This agent works in short bursts and manages its own time — it schedules its own
              wake-ups and builds routines/workflows for repeating work. You don&rsquo;t set a
              schedule here; tell it what you want and it decides when to run.
            </div>
          </>
        ) : null}

        {extraTabs.map((t) => (tab === t.id ? <div key={t.id}>{t.content}</div> : null))}
      </div>
    </div>
  );
}
